/**
 * Zapis NASZYCH zmian do starej bazy WF — panel ITE (ADR-0112b, pakiet 6).
 *
 * To jedyne miejsce w panelu, które pisze do produkcyjnej bazy klienta. Stąd układ okna:
 * próba na sucho jest jedynym wejściem, a przycisk zapisu na ostro pojawia się dopiero po
 * obejrzeniu jej wyniku. Nie z ostrożności „na wszelki wypadek" — bez zobaczenia wartości
 * PRZED i PO nie da się wychwycić pomyłki w mapowaniu, a pomyłka tutaj nadpisuje cudze dane.
 *
 * ── CZEGO PRÓBA NIE ROBI ──
 * Nie budzi programu u klienta. Plan powstaje po naszej stronie z danych, które już mamy,
 * więc nie istnieje tryb „program pisze, ale niby nie" — program dostaje wyłącznie przebiegi
 * na ostro i jedyne, co robi, to zapisuje.
 *
 * ── PRÓG BEZPIECZEŃSTWA ──
 * Pole „najwyżej rekordów" to nie ozdoba: przebieg ruszający więcej rekordów przerywa się
 * PRZED pierwszym zapisem. Sprawdzany jest dwa razy — tutaj, żeby odmowa była natychmiast
 * widoczna, i jeszcze raz w programie u klienta, tuż przed dotknięciem bazy.
 *
 * ── ZAPIS JEST ASYNCHRONICZNY ──
 * „Na ostro" nie zapisuje od razu — zleca zadanie programowi u klienta. Dlatego okno kończy
 * się informacją o zleceniu, a nie o skutku; skutek wraca do dziennika zapisów, gdy program
 * wykona zadanie.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { FlaskConical, Upload } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { RadioGroup } from '@/components/RadioGroup';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';

interface Zmiana {
  field: string;
  from: string | null;
  to: string | null;
}

interface PodgladRekordu {
  sourceRecordId: string;
  changes: Zmiana[];
}

interface RaportZapisu {
  dryRun: boolean;
  jobId?: string;
  records: number;
  fields: number;
  blockedByConflict: number;
  unlinked: number;
  warnings: string[];
  preview?: PodgladRekordu[];
  previewLimit?: number;
}

/** Kartoteki, które umie wypchnąć serwer (ADR-0112b, pakiety 6–7). */
const KARTOTEKI = ['contractor', 'employee'] as const;
type Kartoteka = (typeof KARTOTEKI)[number];

/** Ten sam próg, co po stronie serwera — zgodność jest tu ważniejsza niż elegancja. */
const DOMYSLNY_PROG = 200;

const PREFIX = 'admin.tenants.legacyMigration.push';

export interface LegacyPushModalProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  migrationId: string;
  source: { id: string; name: string; writeEnabled: boolean } | null;
}

/** Pusta wartość czytelnie — inaczej „zmieniam na nic" wygląda jak urwany tekst. */
function wartosc(v: string | null, pusto: string): string {
  return v === null || v === '' ? pusto : v;
}

export function LegacyPushModal({
  open,
  onClose,
  tenantId,
  migrationId,
  source,
}: LegacyPushModalProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [entity, setEntity] = useState<Kartoteka>('contractor');
  const [prog, setProg] = useState<string>(String(DOMYSLNY_PROG));
  const [raport, setRaport] = useState<RaportZapisu | null>(null);
  const [potwierdzenie, setPotwierdzenie] = useState(false);

  const sciezka = source
    ? `/admin/tenants/${tenantId}/legacy-migration/${migrationId}/sources/${source.id}/push/${entity}`
    : null;

  const nazwaKartoteki = t(`admin.tenants.legacyMigration.run.entity.${entity}`);
  const progLiczba = Number.parseInt(prog, 10);
  const progPoprawny = Number.isFinite(progLiczba) && progLiczba > 0;

  const uruchom = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const res = await api.post<RaportZapisu>(sciezka!, { dryRun, maxRecords: progLiczba });
      return res.data;
    },
    onSuccess: (res) => {
      setRaport(res);
      setPotwierdzenie(false);
      if (!res.dryRun) {
        toast.show({
          variant: 'success',
          title: t(`${PREFIX}.queued`, { count: res.records }),
        });
      }
    },
    onError: (err) =>
      toast.show({
        variant: 'error',
        title: t(
          `admin.tenants.legacyMigration.errors.${extractErrorCode(err)}`,
          t(`${PREFIX}.failed`),
        ),
      }),
  });

  const zamknij = () => {
    setRaport(null);
    setPotwierdzenie(false);
    onClose();
  };

  // Wynik próby dotyczy KONKRETNEJ kartoteki i KONKRETNEGO progu — po zmianie któregokolwiek
  // przestaje cokolwiek znaczyć, więc znika razem z przyciskiem zapisu na ostro.
  const uniewaznijProbe = () => {
    setRaport(null);
    setPotwierdzenie(false);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={zamknij}
        size="lg"
        closeOnBackdrop={!uruchom.isPending}
        closeOnEsc={!uruchom.isPending}
        title={t(`${PREFIX}.title`, { name: source?.name ?? '' })}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--text-secondary)]">{t(`${PREFIX}.intro`)}</p>

          {source && !source.writeEnabled && (
            <Alert tone="warning" title={t(`${PREFIX}.writeOffTitle`)}>
              {t(`${PREFIX}.writeOffBody`)}
            </Alert>
          )}

          <RadioGroup<Kartoteka>
            name="legacy-push-entity"
            legend={t('admin.tenants.legacyMigration.run.entityLegend')}
            value={entity}
            onChange={(nowa) => {
              setEntity(nowa);
              uniewaznijProbe();
            }}
            options={KARTOTEKI.map((k) => ({
              value: k,
              label: t(`admin.tenants.legacyMigration.run.entity.${k}`),
            }))}
            data-testid="legacy-push-entity"
          />

          <Input
            className="w-56"
            type="number"
            min={1}
            label={t(`${PREFIX}.limitLabel`)}
            hint={t(`${PREFIX}.limitHint`)}
            value={prog}
            onChange={(e) => {
              setProg(e.target.value);
              uniewaznijProbe();
            }}
          />

          {raport && (
            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium">
                {raport.dryRun
                  ? t(`${PREFIX}.dryResult`, { records: raport.records, fields: raport.fields })
                  : t(`${PREFIX}.liveResult`, { records: raport.records })}
              </span>

              {raport.records === 0 && <Alert tone="info">{t(`${PREFIX}.nothingToPush`)}</Alert>}

              {raport.blockedByConflict > 0 && (
                <Alert tone="warning">
                  {t(`${PREFIX}.blocked`, { count: raport.blockedByConflict })}
                </Alert>
              )}

              {raport.unlinked > 0 && (
                <Alert tone="info">{t(`${PREFIX}.unlinked`, { count: raport.unlinked })}</Alert>
              )}

              {raport.warnings.map((w) => (
                <Alert key={w} tone="info">
                  {w}
                </Alert>
              ))}

              {raport.dryRun && raport.preview && raport.preview.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium">{t(`${PREFIX}.previewTitle`)}</span>
                  <div className="max-h-72 overflow-auto rounded border border-[var(--border)]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-[var(--surface-2)]">
                        <tr>
                          <th className="p-2 text-left font-medium">
                            {t(`${PREFIX}.previewRecord`)}
                          </th>
                          <th className="p-2 text-left font-medium">
                            {t(`${PREFIX}.previewField`)}
                          </th>
                          <th className="p-2 text-left font-medium">
                            {t(`${PREFIX}.previewFrom`)}
                          </th>
                          <th className="p-2 text-left font-medium">{t(`${PREFIX}.previewTo`)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {raport.preview.flatMap((rekord) =>
                          rekord.changes.map((zmiana) => (
                            <tr
                              key={`${rekord.sourceRecordId}:${zmiana.field}`}
                              className="border-t border-[var(--border)]"
                            >
                              <td className="p-2 font-mono text-xs">{rekord.sourceRecordId}</td>
                              <td className="p-2">{zmiana.field}</td>
                              <td className="p-2 text-[var(--text-secondary)]">
                                {wartosc(zmiana.from, t(`${PREFIX}.empty`))}
                              </td>
                              <td className="p-2 font-medium">
                                {wartosc(zmiana.to, t(`${PREFIX}.empty`))}
                              </td>
                            </tr>
                          )),
                        )}
                      </tbody>
                    </table>
                  </div>
                  {raport.previewLimit !== undefined && raport.records > raport.previewLimit && (
                    <p className="text-xs text-[var(--text-secondary)]">
                      {t(`${PREFIX}.previewTruncated`, { shown: raport.previewLimit })}
                    </p>
                  )}
                </div>
              )}

              {!raport.dryRun && <Alert tone="info">{t(`${PREFIX}.asyncHint`)}</Alert>}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={zamknij}>
              {t('common.close')}
            </Button>
            <Button
              variant="secondary"
              leftIcon={<FlaskConical className="size-4" />}
              onClick={() => uruchom.mutate(true)}
              disabled={uruchom.isPending || !progPoprawny}
            >
              {t(`${PREFIX}.dryRun`)}
            </Button>
            {/*
              Zapis na ostro pojawia się WYŁĄCZNIE po próbie i tylko wtedy, gdy jest co pisać.
              Przycisk bez pokrycia w planie kusiłby do kliknięcia „na wszelki wypadek".
            */}
            {raport?.dryRun && raport.records > 0 && (
              <Button
                leftIcon={<Upload className="size-4" />}
                onClick={() => setPotwierdzenie(true)}
                disabled={uruchom.isPending}
              >
                {t(`${PREFIX}.live`)}
              </Button>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={potwierdzenie}
        title={t(`${PREFIX}.confirm.title`)}
        message={t(`${PREFIX}.confirm.message`, {
          records: raport?.records ?? 0,
          fields: raport?.fields ?? 0,
          entity: nazwaKartoteki,
          name: source?.name ?? '',
        })}
        confirmLabel={t(`${PREFIX}.confirm.ok`)}
        variant="danger"
        confirming={uruchom.isPending}
        onConfirm={() => uruchom.mutate(false)}
        onClose={() => setPotwierdzenie(false)}
      />
    </>
  );
}
